import { Button, ButtonProps, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import IconifyIcon from 'components/base/IconifyIcon';
import { useStartSandbox } from 'hooks/useStartSandbox';

type TryFullPlanButtonProps = Omit<ButtonProps, 'onClick' | 'children'> & {
  /**
   * Render just the rocket icon as a compact circular button (with a tooltip +
   * aria-label) instead of the full text label — handy in dense CTA clusters.
   */
  iconOnly?: boolean;
};

/**
 * Reusable entry point that launches an interactive full-plan (Heirloom) demo.
 * Drop it anywhere — dashboard upsell, a locked-feature paywall, or a public
 * marketing surface.
 */
const TryFullPlanButton = ({ disabled, iconOnly = false, ...props }: TryFullPlanButtonProps) => {
  const { t } = useTranslation();
  const { start, starting, turnstileSlot } = useStartSandbox();
  const label = starting ? t('sandbox.cta.starting') : t('sandbox.cta.try');
  const rocket = <IconifyIcon icon="solar:rocket-2-bold-duotone" sx={{ fontSize: 20 }} />;

  if (iconOnly) {
    return (
      <>
        <Tooltip title={label}>
          {/* span keeps the tooltip working while the button is disabled mid-start */}
          <span>
            <Button
              variant="soft"
              color="warning"
              shape="circle"
              aria-label={label}
              {...props}
              onClick={() => start()}
              disabled={disabled || starting}
            >
              {rocket}
            </Button>
          </span>
        </Tooltip>
        {turnstileSlot}
      </>
    );
  }

  return (
    <>
      <Button
        variant="contained"
        color="warning"
        startIcon={rocket}
        {...props}
        onClick={() => start()}
        disabled={disabled || starting}
      >
        {label}
      </Button>
      {turnstileSlot}
    </>
  );
};

export default TryFullPlanButton;
